import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * AwayMessagePanel — M2.3.6
 * Config messaggio di assenza WhatsApp gestito da Sarabi OS.
 * Timezone fissa Europe/Rome (server-side).
 */

const DAY_NAMES = [
  "Lunedì", "Martedì", "Mercoledì", "Giovedì",
  "Venerdì", "Sabato", "Domenica",
];

function _emptyDay() {
  return { closed: true, intervals: [] };
}

function _defaultSchedule() {
  const s = {};
  for (let d = 0; d < 7; d++) s[String(d)] = _emptyDay();
  return s;
}

function DayEditor({ day, name, entry, onChange, onCopyClick }) {
  const closed = !!entry.closed;
  const intervals = entry.intervals || [];

  const setClosed = (v) => onChange({ closed: v, intervals: v ? [] : intervals });
  const setInterval = (i, idx, val) => {
    const next = intervals.map((iv, k) =>
      k === i ? (idx === 0 ? [val, iv[1]] : [iv[0], val]) : iv);
    onChange({ closed: false, intervals: next });
  };
  const addInterval = () => onChange({
    closed: false,
    intervals: [...intervals, ["09:00", "18:00"]],
  });
  const removeInterval = (i) => onChange({
    closed: false,
    intervals: intervals.filter((_, k) => k !== i),
  });

  return (
    <div className="border-t border-ink-100 py-3" data-testid={`away-day-${day}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-24 font-medium text-ink-800">{name}</div>
        <label className="inline-flex items-center gap-1 text-sm">
          <input
            type="checkbox"
            checked={closed}
            onChange={(e) => setClosed(e.target.checked)}
            data-testid={`away-day-${day}-closed`}
          />
          Chiuso
        </label>
        {!closed && (
          <button
            type="button"
            className="text-xs text-blue-600 underline"
            onClick={addInterval}
            data-testid={`away-day-${day}-add`}
          >+ Aggiungi fascia</button>
        )}
        <button
          type="button"
          className="text-xs text-ink-500 underline ml-auto"
          onClick={() => onCopyClick(day)}
          data-testid={`away-day-${day}-copy`}
        >Copia orari su altri giorni…</button>
      </div>
      {!closed && intervals.length === 0 && (
        <div className="ml-24 text-xs text-amber-700 mt-1">
          Nessuna fascia impostata — aggiungine una o segna Chiuso.
        </div>
      )}
      {!closed && intervals.map((iv, i) => (
        <div key={i} className="ml-24 mt-2 flex items-center gap-2">
          <input
            type="time"
            className="input w-28"
            value={iv[0]}
            onChange={(e) => setInterval(i, 0, e.target.value)}
            data-testid={`away-day-${day}-iv-${i}-open`}
          />
          <span>—</span>
          <input
            type="time"
            className="input w-28"
            value={iv[1]}
            onChange={(e) => setInterval(i, 1, e.target.value)}
            data-testid={`away-day-${day}-iv-${i}-close`}
          />
          <button
            type="button"
            className="text-xs text-red-600 underline ml-2"
            onClick={() => removeInterval(i)}
            data-testid={`away-day-${day}-iv-${i}-remove`}
          >Rimuovi</button>
        </div>
      ))}
    </div>
  );
}

function CopyDialog({ sourceDay, onCopy, onClose }) {
  const [selected, setSelected] = useState(() => new Set());
  const toggle = (d) => {
    const next = new Set(selected);
    if (next.has(d)) next.delete(d); else next.add(d);
    setSelected(next);
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          data-testid="away-copy-dialog">
      <div className="bg-white rounded-lg p-5 max-w-sm w-full">
        <h3 className="font-semibold mb-3">Copia orari di {DAY_NAMES[sourceDay]} su:</h3>
        <div className="space-y-2 mb-4">
          {DAY_NAMES.map((n, d) => (
            d === sourceDay ? null : (
              <label key={d} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selected.has(d)}
                  onChange={() => toggle(d)}
                  data-testid={`away-copy-target-${d}`}
                />
                {n}
              </label>
            )
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            data-testid="away-copy-cancel"
          >Annulla</button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => onCopy(Array.from(selected))}
            disabled={selected.size === 0}
            data-testid="away-copy-confirm"
          >Copia</button>
        </div>
      </div>
    </div>
  );
}

export function AwayMessagePanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [cooldownHours, setCooldownHours] = useState(4);
  const [schedule, setSchedule] = useState(_defaultSchedule());
  const [timezone, setTimezone] = useState("Europe/Rome");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [copySourceDay, setCopySourceDay] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/messaging/away/config");
      setEnabled(!!r.data.enabled);
      setMessage(r.data.message || "");
      setCooldownHours(Number(r.data.cooldown_hours || 4));
      const sch = r.data.schedule && Object.keys(r.data.schedule).length > 0
        ? r.data.schedule : _defaultSchedule();
      // Ensure all 7 days present
      for (let d = 0; d < 7; d++) {
        if (!sch[String(d)]) sch[String(d)] = _emptyDay();
      }
      setSchedule(sch);
      setTimezone(r.data.timezone || "Europe/Rome");
      setUpdatedAt(r.data.updated_at || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateDay = (day, newEntry) => {
    setSchedule({ ...schedule, [String(day)]: newEntry });
  };

  const copyToDays = (targetDays) => {
    if (copySourceDay === null) return;
    const src = schedule[String(copySourceDay)];
    const next = { ...schedule };
    for (const d of targetDays) next[String(d)] = JSON.parse(JSON.stringify(src));
    setSchedule(next);
    setCopySourceDay(null);
    toast.success(`Copiato su ${targetDays.length} giorno(i).`);
  };

  const save = async () => {
    if (enabled && !message.trim()) {
      const ok = window.confirm(
        "Away attivo ma messaggio vuoto → nessun invio avverrà. Salvare comunque?");
      if (!ok) return;
    }
    // Validate schedule
    for (let d = 0; d < 7; d++) {
      const entry = schedule[String(d)] || _emptyDay();
      if (!entry.closed) {
        for (const iv of (entry.intervals || [])) {
          if (!iv[0] || !iv[1] || iv[0] === iv[1]) {
            toast.error(`${DAY_NAMES[d]}: fascia oraria non valida.`);
            return;
          }
        }
      }
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/messaging/away/config", {
        enabled, message, cooldown_hours: cooldownHours, schedule,
      });
      setEnabled(!!r.data.enabled);
      setMessage(r.data.message || "");
      setCooldownHours(Number(r.data.cooldown_hours || 4));
      setSchedule(r.data.schedule || _defaultSchedule());
      setUpdatedAt(r.data.updated_at || null);
      toast.success("Configurazione salvata.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="card-elevated p-5 mt-6"
      data-testid="away-message-panel"
    >
      <div className="flex justify-between items-start gap-4">
        <div>
          <h2 className="text-lg font-semibold text-ink-900">
            Messaggio di assenza WhatsApp
          </h2>
          <p className="text-sm text-ink-500 mt-1">
            Automazione gestita da <strong>Sarabi OS</strong>. Fuso orario
            fisso: <code>{timezone}</code>. Fuori orario prevale sul
            welcome auto-reply. Disattiva il messaggio di assenza nella
            WhatsApp Business App dopo il salvataggio.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 select-none">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={loading || saving}
            data-testid="away-enabled-toggle"
            className="h-5 w-5"
          />
          <span className="text-sm font-medium text-ink-800">
            {enabled ? "Attivo" : "Disattivato"}
          </span>
        </label>
      </div>

      <div className="mt-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-ink-800 mb-1">
            Messaggio di assenza
          </label>
          <textarea
            className="input w-full min-h-[100px] font-mono text-sm"
            placeholder="Es. Siamo attualmente chiusi. Ti risponderemo appena riapriamo!"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={loading || saving}
            maxLength={4096}
            data-testid="away-message-input"
          />
          <div className="text-xs text-ink-500 mt-1">
            {(message || "").length} / 4096 caratteri
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-ink-800"
                 htmlFor="away-cooldown">
            Non ripetere entro (ore)
          </label>
          <input
            id="away-cooldown"
            type="number"
            min={0}
            max={168}
            className="input w-24"
            value={cooldownHours}
            onChange={(e) => setCooldownHours(Number(e.target.value) || 0)}
            disabled={loading || saving}
            data-testid="away-cooldown-input"
          />
          <span className="text-xs text-ink-500">
            Default: 4. Range 0–168 (1 settimana). 0 = nessun cooldown.
          </span>
        </div>

        <div className="border rounded-md p-3 bg-ink-50/40" data-testid="away-schedule-editor">
          <h3 className="font-medium text-ink-800 mb-1">Orari di attività</h3>
          <p className="text-xs text-ink-500 mb-2">
            Fuori dagli orari indicati, il messaggio di assenza sarà inviato.
            Puoi impostare più fasce per giorno o segnare un giorno come chiuso.
            Fasce che attraversano mezzanotte (es. 22:00–02:00) sono supportate.
          </p>
          {DAY_NAMES.map((name, d) => (
            <DayEditor
              key={d}
              day={d}
              name={name}
              entry={schedule[String(d)] || _emptyDay()}
              onChange={(next) => updateDay(d, next)}
              onCopyClick={setCopySourceDay}
            />
          ))}
        </div>

        {enabled && !message.trim() && (
          <div className="bg-amber-50 border-l-4 border-amber-500 rounded p-3 text-sm text-amber-900"
               data-testid="away-empty-warning">
            Away attivo ma messaggio vuoto → nessun invio avverrà.
          </div>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between">
        <div className="text-xs text-ink-500">
          {updatedAt
            ? `Ultimo aggiornamento: ${new Date(updatedAt).toLocaleString("it-IT")}`
            : "Non ancora configurato."}
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={save}
          disabled={loading || saving}
          data-testid="away-save-btn"
        >
          {saving ? "Salvo…" : "Salva configurazione"}
        </button>
      </div>

      {copySourceDay !== null && (
        <CopyDialog
          sourceDay={copySourceDay}
          onCopy={copyToDays}
          onClose={() => setCopySourceDay(null)}
        />
      )}
    </section>
  );
}

export default AwayMessagePanel;
